import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { environment } from '@env/environment';
import { HttpPuebloClient } from '@http/http_pueblo_client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ColorService } from '@services/color.service';
import { LoggingService } from '@services/logging.service';
import { PlatformService } from '@services/platform.service';
import { DEFAULT_THEME } from '@utils/defaults';
import { isStringifyEquals } from '@utils/rxjs-utils';
import { NotificationPlatform } from 'pueblo-types';
import {
  EMPTY,
  catchError,
  combineLatest,
  concatMap,
  defer,
  distinctUntilChanged,
  filter,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import * as actions from './user-actions';
import { selectAppUser, selectUsersLiveReloading } from './user-selectors';
import { UserState } from './user-state';

@Injectable()
export class UserEffects {
  private readonly actions$ = inject(Actions);
  private readonly colorService = inject(ColorService);
  private readonly logger = inject(LoggingService);
  private readonly platform = inject(PlatformService);
  private readonly puebloClient = inject(HttpPuebloClient);
  private readonly snackBar = inject(MatSnackBar);
  private readonly userStore = inject(Store<UserState>);

  readonly userLoggedIn$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.userLoggedIn),
      tap(({ resourceName }) => {
        this.logger.log3('[Effect] User logged in', resourceName);
      }),
      map(({ resourceName }) => {
        return actions.fetchAppUser({
          resourceName,
          forceGet: true,
          newLogin: true,
        });
      }),
    );
  });

  readonly fetchAllVerifiedPhones$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.userLoggedIn),
      tap(() => {
        this.logger.log3(`[Effect] Fetch all users summary`);
      }),
      concatMap(() => {
        return this.puebloClient.getVerifiedPhones().pipe(
          map((verifiedPhones) =>
            actions.fetchVerifiedPhonesSuccess({ verifiedPhones }),
          ),
          tap((usersSummary) => {
            this.logger.log3(
              `[Effect] Fetched all users summary success`,
              usersSummary,
            );
          }),
          catchError((message) => {
            this.logger.error(message);
            return of(actions.fetchVerifiedPhonesFail({ message }));
          }),
        );
      }),
    );
  });

  readonly fetchAllUserConfigs$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.fetchAppUserSuccess),
      tap(() => {
        this.logger.log3(`[Effect] Fetch all user configs`);
      }),
      distinctUntilChanged(isStringifyEquals),
      map(({ appUser }) =>
        actions.batchFetchUsers({
          resourceNames: appUser.userConfigs.map(
            (config) => config.resourceName,
          ),
        }),
      ),
    );
  });

  readonly fetchAppUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.fetchAppUser),
      tap(({ resourceName }) => {
        this.logger.log3('[Effect] Fetching current user', resourceName);
      }),
      withLatestFrom(this.userStore.select(selectUsersLiveReloading)),
      mergeMap(([{ resourceName, forceGet, newLogin }, usersLiveReloading]) => {
        if (!forceGet && usersLiveReloading.has(resourceName)) return EMPTY;
        usersLiveReloading.add(resourceName);
        return this.puebloClient.getAppUser(resourceName, { newLogin }).pipe(
          tap((appUser) => {
            this.logger.log3('[Effect] Fetched current user');
            this.colorService.setTheme(
              appUser.preferences?.theme ?? DEFAULT_THEME,
            );
          }),
          map((appUser) => actions.fetchAppUserSuccess({ appUser })),
          catchError((message) => {
            this.logger.error(message);
            return of(actions.fetchAppUserFail({ resourceName, message }));
          }),
        );
      }),
    );
  });

  readonly pingAppUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.pingAppUser),
      tap(({ resourceName }) => {
        this.logger.log3('[Effect] Ping current user', resourceName);
      }),
      mergeMap(({ resourceName }) => {
        return this.puebloClient
          .getAppUser(resourceName)
          .pipe(map((appUser) => actions.fetchAppUserSuccess({ appUser })));
      }),
      catchError((message) => {
        this.logger.error(message);
        return of(actions.fetchAppUserFail({ resourceName: '', message }));
      }),
    );
  });

  readonly updateAppUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.updateAppUser),
      tap(({ appUser }) => {
        this.logger.log3('[Effect] Update app user ', JSON.stringify(appUser));
      }),
      withLatestFrom(this.userStore.select(selectAppUser)),
      map(([{ appUser, hooks }, originalAppUser]) => {
        hooks?.onPending?.(appUser);
        return actions.updatePendingAppUser({
          appUser,
          originalAppUser,
          hooks,
        });
      }),
    );
  });

  readonly updatePendingAppUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.updatePendingAppUser),
      tap(() => {
        this.logger.log3(`[Effect] Update current user`);
      }),
      concatMap(({ appUser, originalAppUser, hooks }) =>
        this.puebloClient.updateAppUser(appUser, originalAppUser).pipe(
          tap((user) => {
            this.logger.log3('[Effect] Updated app user');
            hooks?.onSuccess?.(user);
          }),
          map((appUser) => actions.updateAppUserSuccess({ appUser })),
          catchError((message) => {
            this.logger.error(message);
            hooks?.onFailure?.(message);
            return of(actions.updateAppUserFail({ message, originalAppUser }));
          }),
        ),
      ),
    );
  });

  readonly fetchUser$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.fetchUser),
      tap(({ resourceName }) => {
        this.logger.log3(`[Effect] Fetch user`, resourceName);
      }),
      withLatestFrom(
        combineLatest([
          this.userStore.select(selectUsersLiveReloading),
          this.userStore.select(selectAppUser),
        ]),
      ),
      mergeMap(([{ resourceName }, [usersLiveReloading, appUser]]) => {
        if (usersLiveReloading.has(resourceName)) return EMPTY;
        usersLiveReloading.add(resourceName);
        return this.puebloClient.getUser(resourceName).pipe(
          map((user) => actions.fetchUserSuccess({ user, appUser })),
          tap(() => {
            this.logger.log3(`[Effect] Fetched user`, resourceName);
          }),
          catchError((message) => {
            this.logger.error(message);
            return of(actions.fetchUserFail({ resourceName, message }));
          }),
        );
      }),
    );
  });

  readonly batchFetchUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.batchFetchUsers),
      tap(({ resourceNames }) => {
        this.logger.log3(`[Effect] Batch fetch users`, resourceNames);
      }),
      withLatestFrom(
        combineLatest([
          this.userStore.select(selectUsersLiveReloading),
          this.userStore.select(selectAppUser),
        ]),
      ),
      mergeMap(([{ resourceNames }, [usersLiveReloading, appUser]]) => {
        const fetchUsers = resourceNames.filter(
          (resourceName) => !usersLiveReloading.has(resourceName),
        );
        if (!fetchUsers.length) return EMPTY;
        fetchUsers.forEach((resourceName) => {
          usersLiveReloading.add(resourceName);
        });

        return this.puebloClient.batchGetUsers(fetchUsers).pipe(
          map((users) => actions.batchFetchUsersSuccess({ users, appUser })),
          tap(({ users }) => {
            this.logger.log3(
              `[Effect] Batch fetched users`,
              users.map((user) => user.resourceName).join(', '),
            );
          }),
          catchError((message) => {
            this.logger.error(message);
            return of(actions.batchFetchUsersFail({ message }));
          }),
        );
      }),
    );
  });

  readonly displayErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          actions.fetchAppUserFail,
          actions.updateAppUserFail,
          actions.fetchUserFail,
          actions.batchFetchUsersFail,
          actions.fetchVerifiedPhonesFail,
        ),
        mergeMap(({ message }) => {
          // TODO: Return better/human-readable error messages.
          this.snackBar.open(message);
          return EMPTY;
        }),
      );
    },
    { dispatch: false },
  );

  readonly requestNotificationPermission$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.fetchAppUserSuccess),
      filter(() => Capacitor.isNativePlatform()),
      tap(() => {
        this.logger.log3(`[Effect] Request notification permission`);
      }),
      concatMap(({ appUser }) =>
        defer(async () => {
          let permission = await FirebaseMessaging.checkPermissions();
          if (
            appUser.preferences.notificationPlatform ===
              NotificationPlatform.PUSH &&
            (permission.receive === 'prompt' ||
              permission.receive === 'prompt-with-rationale')
          ) {
            permission = await FirebaseMessaging.requestPermissions();
          }
          if (permission.receive !== 'granted') {
            return actions.requestNotificationPermissionFail();
          }

          const token = (
            await FirebaseMessaging.getToken({
              vapidKey: environment.firebase.vapidKey,
            })
          ).token;

          if (!token) {
            return actions.requestNotificationPermissionFail();
          }

          if (appUser.notificationTokens.map((t) => t.token).includes(token)) {
            return actions.requestNotificationPermissionSuccess();
          }

          const notificationTokens = [
            ...appUser.notificationTokens,
            {
              token,
              platform: this.platform.debugString(),
              errorCount: 0,
            },
          ];

          return actions.updateAppUser({
            appUser: { ...appUser, notificationTokens },
          });
        }),
      ),
    );
  });

  readonly refreshUserLocation$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.refreshUserLocation),
      tap(() => {
        this.logger.log3(`[Effect] Request user location`);
      }),
      concatMap(() => from(Geolocation.getCurrentPosition())),
      map((position) => {
        return actions.updateUserLocationSuccess({
          granted: true,
          location: {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          },
        });
      }),
      catchError((error) => {
        return of(
          actions.updateUserLocationFail({
            error: 'Failed to request permissions.',
          }),
        );
      }),
    );
  });

  readonly recordInteraction$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.recordInteractions),
      tap(() => {
        this.logger.log3(`[Effect] Recording user interactions`);
      }),
      withLatestFrom(this.userStore.select(selectAppUser)),
      switchMap(([request, appUser]) => {
        if (appUser.isAnon) return EMPTY;
        return this.puebloClient.recordInteractions(request);
      }),
      map(() => actions.recordInteractionsSuccess()),
      catchError((error) => {
        return of(
          actions.recordInteractionsFail({
            error: 'Failed to record interactions.',
          }),
        );
      }),
    );
  });
}
